import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Repeat, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n, c) => `${c} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const MAX_POLLS = 20;

const STATUS_META = {
  active: { label: 'Active', cls: 'bg-emerald-50 text-emerald-700' },
  pending: { label: 'Awaiting first payment', cls: 'bg-amber-50 text-amber-700' },
  past_due: { label: 'Payment overdue', cls: 'bg-red-50 text-red-600' },
  paused: { label: 'Paused', cls: 'bg-gray-100 text-gray-600' },
  cancelled: { label: 'Cancelled', cls: 'bg-gray-100 text-gray-500' },
};

const Shell = ({ children }) => (
  <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4 sm:p-6" data-testid="subscription-manage-page">
    {children}
    <a href="https://bookingflows.com" className="mt-6 text-[11px] text-gray-400 hover:text-gray-500 transition-colors">
      Powered by <span className="font-semibold text-gray-500">BookingFlows</span>
    </a>
  </div>
);

/** Public subscription manage page — customers land here after subscribing.
 *  Shows plan + status + latest invoice and lets them request cancellation
 *  (operator confirms — basic, org-managed flow). */
export default function SubscriptionManagePage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [sub, setSub] = useState(null);
  const [error, setError] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const polls = useRef(0);

  const justPaid = params.get('paid') === '1' || !!params.get('session_id');

  const verify = useCallback(async () => {
    try {
      const r = await axios.post(`${BACKEND_URL}/api/v2/public/subscriptions/manage/${token}/verify`);
      setSub(r.data);
      return r.data;
    } catch {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/v2/public/subscriptions/manage/${token}`);
        setSub(r.data);
        return r.data;
      } catch { setError(true); }
    }
    return null;
  }, [token]);

  useEffect(() => { verify(); }, [verify]);

  useEffect(() => {
    if (!sub || sub.status !== 'pending' || !justPaid || polls.current >= MAX_POLLS) return;
    const t = setTimeout(async () => { polls.current += 1; await verify(); }, 6000);
    return () => clearTimeout(t);
  }, [sub, verify, justPaid]);

  const requestCancel = async () => {
    setCancelBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/v2/public/subscriptions/manage/${token}/request-cancel`);
      setSub(r.data);
      setConfirming(false);
    } catch { /* keep page usable */ }
    setCancelBusy(false);
  };

  if (!sub && !error) {
    return (
      <Shell>
        <Loader2 className="w-6 h-6 text-blue-600 animate-spin" data-testid="subscription-loading" />
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell>
        <div className="w-full max-w-md bg-[#f5f6f8] rounded-[28px] px-8 py-12 text-center" data-testid="subscription-not-found">
          <p className="text-[15px] font-semibold text-gray-900">Subscription not found</p>
          <p className="text-[13px] text-gray-400 mt-1.5">This link may have expired or is incorrect.</p>
        </div>
      </Shell>
    );
  }

  const meta = STATUS_META[sub.status] || STATUS_META.pending;
  const inv = sub.latestInvoice;
  const brand = sub.org?.primaryColor || '#007AFF';

  return (
    <Shell>
      <div className="w-full max-w-md bg-[#f5f6f8] rounded-[28px] p-6 sm:p-8" data-testid="subscription-card">
        <div className="flex items-center gap-3">
          {sub.org?.logoUrl ? (
            <img src={sub.org.logoUrl} alt={sub.org.name} className="w-10 h-10 rounded-xl object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold" style={{ background: brand }}>
              {(sub.org?.name || 'B')[0]}
            </div>
          )}
          <div className="min-w-0">
            <p className="text-[14px] font-semibold text-gray-900 truncate">{sub.org?.name}</p>
            <p className="text-[11px] text-gray-400">Subscription</p>
          </div>
          <span className={`ml-auto px-2.5 py-1 rounded-full text-[11px] font-medium flex-shrink-0 ${meta.cls}`} data-testid="subscription-status-pill">
            {meta.label}
          </span>
        </div>

        <div className="bg-white rounded-2xl p-5 mt-5">
          <div className="flex items-center gap-2 text-gray-400">
            <Repeat className="w-3.5 h-3.5" />
            <span className="text-[11px] uppercase tracking-wide">{sub.intervalLabel} plan</span>
          </div>
          <p className="text-[17px] font-semibold text-gray-900 mt-1.5" data-testid="subscription-plan-name">{sub.serviceName}</p>
          <p className="text-[24px] font-bold text-gray-900 mt-1 tabular-nums">
            {fmt(sub.amount, sub.currency)} <span className="text-[13px] font-normal text-gray-400">/ {sub.interval}</span>
          </p>
          <div className="border-t border-gray-100 mt-4 pt-3 space-y-1.5 text-[12.5px]">
            {sub.customerName && (
              <p className="flex justify-between"><span className="text-gray-400">Subscriber</span><span className="text-gray-700">{sub.customerName}</span></p>
            )}
            {sub.status !== 'cancelled' && sub.nextBillingDate && (
              <p className="flex justify-between"><span className="text-gray-400">Next billing</span><span className="text-gray-700" data-testid="subscription-next-billing">{sub.nextBillingDate}</span></p>
            )}
            {sub.startedAt && (
              <p className="flex justify-between"><span className="text-gray-400">Started</span><span className="text-gray-700">{sub.startedAt.slice(0, 10)}</span></p>
            )}
          </div>
        </div>

        {inv && (
          <div className="bg-white rounded-2xl p-4 mt-3" data-testid="subscription-latest-invoice">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[12px] text-gray-400">Latest invoice · {inv.invoiceNumber}</p>
                <p className="text-[14px] text-gray-900 tabular-nums mt-0.5">
                  {fmt(inv.total, inv.currency)}
                  {inv.status === 'paid'
                    ? <span className="text-emerald-600 text-[12px] ml-1.5 inline-flex items-center gap-0.5"><Check className="w-3 h-3" /> paid</span>
                    : <span className="text-amber-600 text-[12px] ml-1.5">awaiting payment</span>}
                </p>
              </div>
              {inv.status === 'pending' && inv.checkoutUrl ? (
                <a href={inv.checkoutUrl} data-testid="subscription-pay-invoice-btn"
                   className="px-4 h-9 inline-flex items-center rounded-full text-white text-[13px] font-medium" style={{ background: brand }}>
                  Pay now
                </a>
              ) : (
                <a href={inv.pageUrl} data-testid="subscription-view-invoice-link" className="text-[12px] text-gray-500 underline underline-offset-2">View</a>
              )}
            </div>
          </div>
        )}

        {sub.status !== 'cancelled' && (
          <div className="mt-5">
            {sub.cancelRequested ? (
              <div className="bg-white rounded-2xl p-4 text-center" data-testid="subscription-cancel-requested">
                <p className="text-[12.5px] text-gray-600">Cancellation requested — {sub.org?.name} will confirm shortly.</p>
                {(sub.org?.email || sub.org?.phone) && (
                  <p className="text-[11.5px] text-gray-600 mt-1.5" data-testid="subscription-cancel-contact">
                    Questions? Contact {sub.org?.name}
                    {sub.org?.email && <> at <a href={`mailto:${sub.org.email}`} className="text-blue-600 hover:underline" data-testid="subscription-org-email">{sub.org.email}</a></>}
                    {sub.org?.phone && <>{sub.org?.email ? ' or ' : ' at '}<a href={`tel:${sub.org.phone}`} className="text-blue-600 hover:underline" data-testid="subscription-org-phone">{sub.org.phone}</a></>}
                  </p>
                )}
              </div>
            ) : confirming ? (
              <div className="bg-white rounded-2xl p-4 text-center">
                <p className="text-[13px] text-gray-700">Request to cancel this subscription?</p>
                <p className="text-[11.5px] text-gray-400 mt-1">{sub.org?.name} will confirm and stop future billing.</p>
                {(sub.org?.email || sub.org?.phone) && (
                  <p className="text-[11px] text-gray-600 mt-1.5" data-testid="subscription-confirm-contact">
                    Prefer to talk it through?
                    {sub.org?.email && <> Email <a href={`mailto:${sub.org.email}`} className="text-blue-600 hover:underline">{sub.org.email}</a></>}
                    {sub.org?.phone && <>{sub.org?.email ? ' or call ' : ' Call '}<a href={`tel:${sub.org.phone}`} className="text-blue-600 hover:underline">{sub.org.phone}</a></>}
                  </p>
                )}
                <div className="flex gap-2 mt-3">
                  <button onClick={() => setConfirming(false)} className="flex-1 h-9 rounded-full bg-gray-100 text-[13px] text-gray-600" data-testid="subscription-cancel-abort-btn">Keep it</button>
                  <button onClick={requestCancel} disabled={cancelBusy} className="flex-1 h-9 rounded-full bg-red-50 text-[13px] text-red-600 font-medium disabled:opacity-60" data-testid="subscription-cancel-confirm-btn">
                    {cancelBusy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Request cancellation'}
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setConfirming(true)}
                data-testid="subscription-request-cancel-btn"
                className="w-full text-center text-[12px] text-gray-400 hover:text-gray-600 underline underline-offset-2 transition-colors"
              >
                Request cancellation
              </button>
            )}
          </div>
        )}
      </div>
    </Shell>
  );
}
